import React, { useState, ChangeEvent, FormEvent } from "react";
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Paper,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useHelp from "@/hooks/use-help";

interface FormData {
  question: string;
  attachment: File | null;
}

const HelpPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const primaryColor = "#644980";
  const [formData, setFormData] = useState<FormData>({
    question: "",
    attachment: null,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { sendQuestion, isLoading, error } = useHelp();
  const authUser = useSelector((state: RootState) => state.auth.authUser);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] || null;
    setFormData((prevState) => ({
      ...prevState,
      attachment: file,
    }));
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const response = await sendQuestion(formData.question, formData.attachment);
    if (response) {
      setIsSubmitted(true);
      setFormData({ question: "", attachment: null });
    }
  };

  if (isSubmitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, direction: "rtl" }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            borderRadius: 3,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 80, color: primaryColor, mb: 2 }} />
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: primaryColor, mb: 2 }}
          >
            {t("help.confirmation.title")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#555" }}>
            {t("help.confirmation.body")}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, direction: "rtl" }}>
      <Paper elevation={0} sx={{ p: 3, mb: 10, borderRadius: 3 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: primaryColor, fontWeight: "bold", textAlign: "center" }}
        >
          {t("help.welcome")} {authUser?.nickName || t("help.host")}! 👋
        </Typography>
        <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
          {t("help.prompt")}
        </Typography>

        {error && (
          <Typography
            variant="body2"
            sx={{ color: "red", textAlign: "center", mb: 2 }}
          >
            {t("help.errors.sendError")}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}
          >
            {t("help.writeQuestion")}
          </Typography>
          <TextField
            name="question"
            value={formData.question}
            onChange={handleChange}
            multiline
            rows={5}
            fullWidth
            required
            inputProps={{
              style: { direction: "rtl", textAlign: "right" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                direction: "rtl",
                marginBottom: 2,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
              },
            }}
          />
          {formData.attachment && (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: primaryColor, mb: 2 }}
            >
              {t("help.attachedFile")} {formData.attachment.name}
            </Typography>
          )}
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{
              mb: 2,
              backgroundColor: primaryColor,
              "&:hover": { backgroundColor: "#502d73" },
              textTransform: "none",
            }}
          >
            {t("help.attachFile")}
            <input
              type="file"
              name="attachment"
              hidden
              onChange={handleFileChange}
            />
          </Button>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: primaryColor,
              "&:hover": { backgroundColor: "#502d73" },
              textTransform: "none",
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              t("help.sendQuestion")
            )}
          </Button>
        </Box>

        <Typography
          variant="body2"
          sx={{ mt: 4, textAlign: "center", color: "#555" }}
        >
          {t("help.note")} {t("help.confirmation.title")}
        </Typography>
      </Paper>
    </Container>
  );
};

export default HelpPage;
